/**
 * Wishlist Engine - Luxury Standard Implementation
 * Pure JavaScript localStorage-based wishlist system
 * Inspired by Benuta.com & Instagram
 */

class WishlistEngine {
  constructor() {
    this.storageKey = 'shopify_wishlist_items';
    this.wishlist = this.loadWishlist();
    this.init();
  }

  init() {
    // Initialize on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Initialize save buttons
    this.initSaveButtons();
    
    // Update header counter
    this.updateHeaderCounter();
    
    // Update all save button states
    this.updateAllSaveButtons();
    
    // Load wishlist grid if on wishlist page
    if (document.getElementById('wishlist-grid-container')) {
      this.loadWishlistGrid();
    }
    
    // Listen for storage changes (cross-tab sync)
    window.addEventListener('storage', (e) => {
      if (e.key === this.storageKey) {
        this.wishlist = this.loadWishlist();
        this.updateHeaderCounter();
        this.updateAllSaveButtons();
        // Reload grid if on wishlist page
        if (document.getElementById('wishlist-grid-container')) {
          this.loadWishlistGrid();
        }
      }
    });

    // Listen for custom wishlist events
    window.addEventListener('wishlistUpdated', () => {
      this.wishlist = this.loadWishlist();
      this.updateHeaderCounter();
      this.updateAllSaveButtons();
      // Reload grid if on wishlist page
      if (document.getElementById('wishlist-grid-container')) {
        this.loadWishlistGrid();
      }
    });
  }

  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      // Ensure it's an array
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('Error loading wishlist:', e);
      return [];
    }
  }

  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
      this.updateHeaderCounter();
      this.updateAllSaveButtons();
      
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('wishlistUpdated', { 
        detail: { wishlist: this.wishlist, count: this.wishlist.length }
      }));
      
      return true;
    } catch (e) {
      console.error('Error saving wishlist:', e);
      return false;
    }
  }

  addProduct(productHandle, productData = {}) {
    // Only store handle as primary identifier
    if (!productHandle) return false;
    
    const exists = this.wishlist.find(item => item.handle === productHandle);
    if (!exists) {
      this.wishlist.push({
        handle: productHandle,
        addedAt: new Date().toISOString(),
        ...productData
      });
      this.saveWishlist();
      return true;
    }
    return false;
  }

  removeProduct(productHandle) {
    if (!productHandle) return false;
    
    const index = this.wishlist.findIndex(item => item.handle === productHandle);
    if (index > -1) {
      this.wishlist.splice(index, 1);
      this.saveWishlist();
      return true;
    }
    return false;
  }

  toggleProduct(productHandle, productData = {}) {
    if (this.isSaved(productHandle)) {
      return this.removeProduct(productHandle);
    } else {
      return this.addProduct(productHandle, productData);
    }
  }

  isSaved(productHandle) {
    return this.wishlist.some(item => item.handle === productHandle);
  }

  getCount() {
    return this.wishlist.length;
  }

  getAllHandles() {
    return this.wishlist.map(item => item.handle);
  }

  getAllProducts() {
    return this.wishlist;
  }

  clear() {
    this.wishlist = [];
    this.saveWishlist();
  }

  updateHeaderCounter() {
    // Update the count badge in the hardcoded header icon
    const badge = document.getElementById('wishlist-count-badge');
    const link = document.getElementById('wishlist-link-header');
    
    if (badge) {
      const count = this.getCount();
      badge.textContent = count;
      badge.style.display = count > 0 ? 'block' : 'none';
      
      // Update link accessibility
      if (link) {
        link.setAttribute('aria-label', `Wishlist (${count} ${count === 1 ? 'item' : 'items'})`);
      }
    }
  }

  updateAllSaveButtons() {
    const buttons = document.querySelectorAll('.wishlist-save-btn');
    buttons.forEach(btn => {
      const handle = btn.getAttribute('data-product-handle');
      if (handle) {
        if (this.isSaved(handle)) {
          btn.classList.add('wishlist-save-btn--saved');
          btn.setAttribute('aria-label', 'Remove from wishlist');
        } else {
          btn.classList.remove('wishlist-save-btn--saved');
          btn.setAttribute('aria-label', 'Add to wishlist');
        }
      }
    });
  }

  initSaveButtons() {
    // Handle existing buttons
    document.querySelectorAll('.wishlist-save-btn').forEach(btn => {
      if (btn.dataset.initialized) return;
      btn.dataset.initialized = 'true';
      
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const handle = btn.getAttribute('data-product-handle');
        if (!handle) return;

        // Get product data from data attributes
        const productData = {
          id: btn.getAttribute('data-product-id') || '',
          title: btn.getAttribute('data-product-title') || '',
          url: btn.getAttribute('data-product-url') || '',
          image: btn.getAttribute('data-product-image') || '',
          price: btn.getAttribute('data-product-price') || ''
        };

        const wasSaved = this.isSaved(handle);
        this.toggleProduct(handle, productData);

        // Add animation class
        btn.classList.add('wishlist-save-btn--animating');
        setTimeout(() => {
          btn.classList.remove('wishlist-save-btn--animating');
        }, 300);

        // Update button state
        if (wasSaved) {
          btn.classList.remove('wishlist-save-btn--saved');
        } else {
          btn.classList.add('wishlist-save-btn--saved');
        }
      });
    });
  }

  /**
   * Load wishlist grid using Section Rendering API
   * This function fetches product cards and displays them on the wishlist page
   */
  async loadWishlistGrid() {
    const grid = document.getElementById('wishlist-grid-container');
    const emptyMsg = document.getElementById('wishlist-empty-msg');
    const loader = document.getElementById('wishlist-loader');

    if (!grid || !emptyMsg || !loader) {
      // Not on wishlist page
      return;
    }

    // 1. Get Handles from localStorage using our method
    const handles = this.getAllHandles();
    console.log('Carpetify Wishlist: Loading items...', handles);
    console.log('Carpetify: Storage key:', this.storageKey);
    console.log('Carpetify: Raw localStorage:', localStorage.getItem(this.storageKey));

    // 2. Handle Empty State
    if (handles.length === 0) {
      if (loader) loader.style.display = 'none';
      if (emptyMsg) emptyMsg.style.display = 'block';
      if (grid) grid.innerHTML = '';
      return;
    }

    // 3. Clear container before loading to prevent duplicates
    if (grid) grid.innerHTML = '';
    if (loader) loader.style.display = 'block';
    if (emptyMsg) emptyMsg.style.display = 'none';

    // Create an array of fetch promises
    const fetchPromises = handles.map(handle => 
      fetch(`/products/${handle}?section_id=api-wishlist-card`)
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status} for ${handle}`);
          }
          return res.text();
        })
        .then(html => {
          console.log(`Carpetify: Fetched HTML for ${handle}`, html.substring(0, 200));
          
          // Parse HTML
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          // Try to find our specific wrapper first
          let card = doc.querySelector('.wishlist-card-response');
          
          // Fallback: Try finding the card container
          if (!card) card = doc.querySelector('.card__container');
          if (!card) card = doc.querySelector('.card-wrapper');
          if (!card) card = doc.querySelector('[class*="card"]');

          if (card) {
            console.log(`Carpetify: Found card for ${handle}`);
            // Wrap in collection__grid__item for proper grid layout (same as collection page)
            const gridItem = document.createElement('div');
            gridItem.className = 'collection__grid__item';
            if (window.settings && window.settings.animations) {
              gridItem.classList.add('scroll-trigger', 'animate--slide-in');
            }
            // Get the card content (the card__container or the whole card structure)
            const cardContent = card.querySelector('.card__container') || card.firstElementChild || card;
            if (cardContent) {
              gridItem.appendChild(cardContent.cloneNode(true));
            } else {
              gridItem.innerHTML = card.innerHTML;
            }
            return gridItem;
          } else {
            console.error(`Carpetify Error: Could not extract card for ${handle}`);
            console.error('Available elements:', Array.from(doc.body.querySelectorAll('*')).map(el => el.className));
            return null;
          }
        })
        .catch(err => {
          console.error(`Carpetify Fetch Error for ${handle}:`, err);
          return null;
        })
    );

    // 4. Render All
    try {
      const results = await Promise.all(fetchPromises);
      const validItems = results.filter(item => item !== null);
      
      console.log(`Carpetify: Rendering ${validItems.length} items`);
      
      if (loader) loader.style.display = 'none';
      
      if (validItems.length === 0) {
        // No valid items
        if (emptyMsg) emptyMsg.style.display = 'block';
        return;
      }

      // Append all items to grid
      validItems.forEach(item => {
        if (item && grid) grid.appendChild(item);
      });
      
      // Re-initialize wishlist buttons for the newly loaded cards
      this.initSaveButtons();
      this.updateAllSaveButtons();
      
      console.log('Carpetify Wishlist: Render Complete.');
    } catch (error) {
      console.error('Carpetify Error rendering wishlist:', error);
      if (loader) loader.style.display = 'none';
      if (emptyMsg) emptyMsg.style.display = 'block';
    }
  }
}

// Initialize global wishlist engine
if (typeof window !== 'undefined') {
  window.wishlistEngine = new WishlistEngine();
}

// Handle dynamic content (AJAX-loaded products)
document.addEventListener('wishlistInit', () => {
  if (window.wishlistEngine) {
    window.wishlistEngine.initSaveButtons();
    window.wishlistEngine.updateAllSaveButtons();
  }
});

// Re-initialize when new content is added (for AJAX updates)
const observer = new MutationObserver(() => {
  if (window.wishlistEngine) {
    window.wishlistEngine.initSaveButtons();
  }
});

if (document.body) {
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

